<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <v-form ref="backupForm" v-model="isFormValid">
    <span class="font-size-18">{{ $t('vmBackups.backupHistoryLog') }}</span>
    <v-col cols="10" class="pl-0">
      <the-nerve-table
        v-if="!errorMessageVmBackup"
        :columns="columns"
        :empty-state="$t('vmBackups.emptyState')"
        :is-row-clickable="false"
        :is-search-enabled="false"
        :is-action-menu-enabled="false"
        :params="params"
        :disable-pagination="true"
        :hide-footer="true"
        store-module="vm-backups"
      />
    </v-col>
    <v-row v-if="errorMessageVmBackup" class="ml-0 mt-2 mb-2">
      <div class="text--disabled">{{ errorMessageVmBackup }}</div>
    </v-row>
    <v-row class="mt-3">
      <v-divider />
    </v-row>
    <v-row v-if="!errorMessageVmBackup && editBackups" class="mt-9 pl-5">
      <span class="font-size-18">{{ $t('vmBackups.newBackup') }}</span>
    </v-row>
    <v-row v-if="!errorMessageVmBackup && editBackups" class="pl-2">
      <v-col lg="4" md="5" sm="8" xs="8" cols="10">
        <v-text-field
          v-model="backupName"
          :rules="[rules.required, rules.name_contains_only_spaces, rules.name_contains_control_character]"
          :disabled="isBackupAddDisabled"
          :data-cy="`iiotVmBackupsBackupNameTextField${id}`"
          label="Backup name"
          @focus="generateBackupName"
        />
      </v-col>
      <v-col>
        <nerve-button
          id="iiotVmBackupsSaveButton"
          :text="$t('vmBackups.saveBtn')"
          :disabled="isBackupAddDisabled || !isFormValid"
          :data-cy="`iiotVmBackupsSaveButton${id}`"
          class="mt-3"
          type-of-btn="action"
          size="normal"
          @click-event="createBackup()"
        />
      </v-col>
    </v-row>
  </v-form>
</template>
<script>
import TheNerveTable from './TheNerveTable.vue';
import NerveButton from './NerveButton.vue';
import { VALIDATION_REGEX } from './helpers/constants';

export default {
  name: 'VmBackups',
  components: {
    TheNerveTable,
    NerveButton,
  },
  props: {
    backupModel: {
      type: Object,
      default: () => ({
        nodeSerialNumber: '',
        vmName: undefined,
      }),
    },
    storeModule: {
      type: String,
      default: '',
    },
    params: {
      type: Object,
      default: () => {},
    },

    isBackupAddDisabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    errorMessageVmBackup: {
      type: String,
      default: '',
    },
    workloadId: {
      type: String,
      default: '',
    },
    versionId: {
      type: String,
      default: '',
    },
    editBackups: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      backupName: '',
      isCreateBtnDisabled: false,
      isFormValid: false,
    };
  },
  computed: {
    rules() {
      return {
        required: (value) => !!value || this.$t('vmBackups.dialogRequired'),
        name_contains_only_spaces: (value) =>
          !VALIDATION_REGEX.STRING_CONTAINS_ONLY_SPACES.test(value) || this.$t('vmBackups.wlNameContainOnlySpaces'),
        name_contains_control_character: (value) =>
          VALIDATION_REGEX.NO_CONTROL_CHARACTERS.test(value) || this.$t('vmBackups.wlNameContainControlCharacter'),
      };
    },
    columns() {
      return [
        {
          text: this.$t('vmBackups.name'),
          value: 'name',
          sortable: false,
          width: '30%',
          isTruncateEnabled: true,
        },
        {
          text: this.$t('vmBackups.started'),
          value: 'started',
          sortable: false,
        },
        {
          text: this.$t('vmBackups.finished'),
          value: 'finished',
          sortable: false,
        },
        {
          text: this.$t('vmBackups.headerProgress'),
          value: 'progress',
          component: {
            sfc: () => import('./ProgressLinearWithStatus.vue'),
            props: {
              page: 'backup',
            },
          },
          sortable: false,
        },
        ...(this.editBackups
          ? [
              {
                text: this.$t('defaultTable.action'),
                value: '',
                width: '0%',
                sortable: false,
                component: {
                  sfc: () => import('./helpers/VmBackupsTableActions.vue'),
                  props: {
                    iconsAction: ['Retry', 'DisabledRetry'],
                    workloadId: this.workloadId,
                    versionId: this.versionId,
                    editBackups: this.editBackups,
                  },
                },
              },
            ]
          : []),
      ];
    },
  },
  watch: {
    backupName() {
      if (!this.backupName) {
        this.isCreateBtnDisabled = true;
        return;
      }
      this.isCreateBtnDisabled = false;
    },
  },
  methods: {
    generateBackupName() {
      if (this.backupName === '' || this.backupName === null || this.backupName === undefined) {
        const timeZoneInfo = this.getCurrentTimeZoneInfo();
        const current = new Date();
        const month = current.getMonth() + 1;
        this.backupName = `${this.backupModel.nodeSerialNumber}-${
          this.backupModel.vmName
        }-backup ${current.getDate()}-${month}-${current.getFullYear()} ${current.getHours()}:${current.getMinutes()}:${current.getSeconds()} GMT${
          timeZoneInfo.timezoneOffset
        }`;
      }
    },
    createBackup() {
      this.$emit('create-vm-backup', { name: this.backupName });
      this.backupName = '';
      this.$refs.backupForm.reset();
    },
    getCurrentTimeZoneInfo() {
      const current = new Date();
      const offset = -current.getTimezoneOffset();
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      // eslint-disable-next-line radix
      const timezoneOffset = (offset >= 0 ? '+' : '-') + parseInt(offset / 60);
      return { timeZone, timezoneOffset };
    },
  },
};
</script>
<style scoped lang="scss">
.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}

.primary-tttech-background-btn-disabled {
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12));
}

.primary-tttech-background-for-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
.tertiary-tttech-background-for-btn {
  background-image: linear-gradient(to bottom, var(--v-tertiary-lighten4), var(--v-tertiary-base));
}

.btn-appearance {
  @extend .primary-tttech-background-for-btn;
  width: 100%;
  height: 40px !important;
  text-transform: none;
  color: var(--v-secondary-base) !important;
  font-weight: bold !important;
  font-size: 16px;
  line-height: 1px;
  flex: 1 0 auto;
}

.btns {
  @extend .btn-appearance;
  margin-top: 76px;
  margin-bottom: 40px;
  &.cancel {
    @extend .tertiary-tttech-background-for-btn;
  }
}

.btns[disabled] {
  background-image: none;
  background-color: #e9e9e9;
  color: #ffffff;
}

.backup-name-position {
  min-width: 200px;
  max-width: 600px;
}

.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}

.primary-tttech-background-btn-disabled {
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12));
}

.font-size-18 {
  font-size: 18px;
}
</style>
